<script setup lang="ts">
import { computed, type PropType } from 'vue';
import { dividerProps, useThemeVars } from 'naive-ui';
import { gaiaFontSize, gaiaFontWeight, gaiaNeutral, gaiaTracking } from '../themes/tokens';

const props = defineProps({
  ...dividerProps,
  /** default = the 2px primary section divider; subtle = the hairline "or"
      separator with a small uppercase label (Login mock). */
  variant: {
    type: String as PropType<'default' | 'subtle'>,
    default: 'default',
  },
});
const themeVars = useThemeVars();
// The subtle label styles naive's own divider title element, so the tokens
// reach it through v-bind rather than GaiaText props.
const labelSize = gaiaFontSize.micro;
const labelWeight = String(gaiaFontWeight.bold);
const labelTracking = gaiaTracking.label;

// `variant` is ours, not n-divider's — keep it off the DOM element.
const passThrough = computed(() => {
  const { variant, ...rest } = props;
  return rest;
});

const mergedThemeOverrides = computed(() => ({
  ...(props.variant === 'subtle'
    ? { color: gaiaNeutral.borderMuted, textColor: gaiaNeutral.textMuted }
    : { color: themeVars.value.primaryColor, fontWeight: String(gaiaFontWeight.semibold) }),
  ...props.themeOverrides,
}));
</script>

<template>
  <!-- The slot is forwarded only when the caller actually passed one: naive
       splits the rule in two around a title whenever a default slot exists,
       empty or not, so an unconditional `<slot />` leaves every title-less
       divider with a gap in the middle. -->
  <n-divider
    v-bind="passThrough"
    :theme-overrides="mergedThemeOverrides"
    :class="{ 'gaia-divider--subtle': props.variant === 'subtle' }"
    :style="props.variant === 'subtle' ? undefined : { fontSize: '18px' }"
  >
    <template v-if="$slots.default" #default><slot /></template>
  </n-divider>
</template>

<style scoped>
/* line thickness is hardcoded to 1px in naive-ui, not themeable */
:deep(.n-divider__line) {
  height: 2px;
}

/* Subtle variant: hairline + the uppercase micro label — the label casing
   and size have no divider theme vars. */
.gaia-divider--subtle :deep(.n-divider__line) {
  height: 1px;
}

.gaia-divider--subtle :deep(.n-divider__title) {
  font-size: v-bind(labelSize);
  font-weight: v-bind(labelWeight);
  letter-spacing: v-bind(labelTracking);
  text-transform: uppercase;
}
</style>
